// ===============================================
// 🛡️ PROTEÇÃO DE ROTAS BASEADA EM CARGO
// ===============================================
// Arquivo: src/components/RoleBasedRoute.js

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const RoleBasedRoute = ({ children, allowedRoles = [], redirectTo = null }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Enquanto carrega, mostrar tela de loading
  if (loading) {
    return (
      <div className="role-loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Verificando permissões...</p>
        </div>
        
        <style jsx>{`
          .role-loading {
            min-height: calc(100vh - 70px);
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            color: #4a5568;
            font-family: 'Inter', 'Segoe UI', sans-serif;
          }
          
          .loading-container {
            text-align: center;
            background: white;
            padding: 40px;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          }
          
          .loading-spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #e2e8f0;
            border-top: 4px solid #667eea;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 20px;
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .loading-container p {
            font-size: 16px;
            margin: 0;
            font-weight: 500;
            color: #4a5568;
          }
        `}</style>
      </div>
    );
  }

  // Se não estiver autenticado, redirecionar para login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Verificar se o usuário tem o cargo necessário
  const userRole = user?.cargo?.toLowerCase();
  const hasPermission = allowedRoles.length === 0 || 
                       allowedRoles.some(role => role.toLowerCase() === userRole);

  if (!hasPermission) {
    // Se há um redirecionamento específico, usar ele
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    // Caso contrário, redirecionar baseado no cargo do usuário
    const defaultRedirect = getDefaultRedirectForRole(userRole);
    return <Navigate to={defaultRedirect} replace />;
  }

  // Se tem permissão, renderizar o componente
  return children;
};

// ===============================================
// 🎯 FUNÇÕES AUXILIARES DE REDIRECIONAMENTO
// ===============================================

/**
 * Retorna a rota padrão baseada no cargo do usuário
 */
const getDefaultRedirectForRole = (userRole) => {
  switch (userRole) {
    case 'administrador':
      return '/administrator';
    case 'expedição':
    case 'expedicao':
      return '/expedition';
    case 'loja':
      return '/receipt';
    default:
      return '/expedition'; // Fallback padrão
  }
};

/**
 * Verifica se usuário tem permissão específica
 */
export const hasRole = (user, requiredRole) => {
  if (!user || !user.cargo) return false;
  return user.cargo.toLowerCase() === requiredRole.toLowerCase();
};

/**
 * Verifica se usuário tem alguma das permissões
 */
export const hasAnyRole = (user, roles) => {
  if (!user || !user.cargo || !Array.isArray(roles)) return false;
  const userRole = user.cargo.toLowerCase();
  return roles.some(role => role.toLowerCase() === userRole);
};

/**
 * Verifica se usuário é administrador
 */
export const isAdmin = (user) => {
  return hasRole(user, 'administrador');
};

/**
 * Verifica se usuário é da expedição
 */
export const isExpedition = (user) => {
  return hasRole(user, 'expedição') || hasRole(user, 'expedicao');
};

/**
 * Verifica se usuário é da loja
 */
export const isStore = (user) => {
  return hasRole(user, 'loja');
};

// ===============================================
// 🚦 COMPONENTES DE PROTEÇÃO ESPECÍFICOS
// ===============================================

/**
 * Rota apenas para administradores
 */
export const AdminRoute = ({ children }) => (
  <RoleBasedRoute allowedRoles={['administrador']}>
    {children}
  </RoleBasedRoute>
);

/**
 * Rota apenas para expedição
 */
export const ExpeditionRoute = ({ children }) => (
  <RoleBasedRoute allowedRoles={['expedição', 'expedicao']}>
    {children}
  </RoleBasedRoute>
);

/**
 * Rota apenas para loja
 */
export const StoreRoute = ({ children }) => (
  <RoleBasedRoute allowedRoles={['loja']}>
    {children}
  </RoleBasedRoute>
);

/**
 * Rota para expedição ou administrador
 */
export const ExpeditionOrAdminRoute = ({ children }) => (
  <RoleBasedRoute allowedRoles={['administrador', 'expedição', 'expedicao']}>
    {children}
  </RoleBasedRoute>
);

/**
 * Rota para qualquer usuário autenticado
 */
export const AnyAuthenticatedRoute = ({ children }) => (
  <RoleBasedRoute allowedRoles={[]}>
    {children}
  </RoleBasedRoute>
);

export default RoleBasedRoute;